import useCreditCardForm from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import { Caption } from '~/shared/Typography';
import { Box, FormControl, WarningIcon, VStack, HStack } from 'native-base';
import { SpIcon } from '~/shared/Icon';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Pressable from '~/shared/Pressable';
import { CreditCardDto } from '~/shared/CreditCardForm/hooks/useCreditCardForm';
import React, { ReactNode } from 'react';
import { I18nReacher, useTheme } from '@springbig/shared';
import { Button, Input } from '~/shared/Theme';
import useSubscriptionPlansHelpers from '~/pages/subscriptions/hooks/useSubscriptionPlansHelpers';

type CreditCardFormProps = {
    submitButtonText?: I18nReacher;
    summary?: string;
    error?: string[] | string;
    onCancel: () => void;
    onProceed: (data: CreditCardDto) => void;
    isLoading?: boolean;
    showCancel?: boolean;
    titleComponent?: ReactNode;
};

const CreditCardForm = ({
    submitButtonText,
    summary,
    error,
    onCancel,
    onProceed,
    isLoading,
    showCancel = true,
    titleComponent,
}: CreditCardFormProps) => {

    const {
        field_background,
        primary,
    } = useTheme();

    const {
        isAmExCard,
        isNumberVisible,
        isNumberFieldFocused,
        numberFieldRef,
        formMethods,
        nameField,
        numberField,
        cvvField,
        expiryField,
        zipField,
        onCardNumberChange,
        onExpiryChange,
        setBooleanState,
        onSubmit,
    } = useCreditCardForm({
        onProceed,
    });

    const { showSuspendedMethodWarning, suspendedPaymentMethods } = useSubscriptionPlansHelpers();

    return (
        <FormControl isInvalid={!!error}>
            { titleComponent && titleComponent }
            <VStack space={4}>
                <FormControl isInvalid={!!formMethods.errors.name}>
                    <FormControl.Label>
                        <Caption text={'credit_card.name'} />
                    </FormControl.Label>
                    <Input
                        {...nameField}
                        isInvalid={!!formMethods.errors.name}
                        isDisabled={isLoading}
                        maxLength={255}
                        returnKeyType={'done'}
                    />
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        { formMethods.errors.name?.message }
                    </FormControl.ErrorMessage>
                </FormControl>

                <FormControl isInvalid={!!formMethods.errors.number}>
                    <FormControl.Label>
                        <Caption text={'credit_card.number'} />
                    </FormControl.Label>
                    { isNumberVisible ?
                        <Input
                            {...numberField}
                            keyboardType={'numberPad'}
                            isInvalid={!!formMethods.errors.number}
                            isDisabled={isLoading}
                            maxLength={19}
                            returnKeyType={'done'}
                            onChangeText={(value) => {

                                onCardNumberChange(value);
                            }}
                            InputRightElement={
                                <Pressable
                                    onPress={() => setBooleanState('isNumberVisible', !isNumberVisible)}
                                    px={2}
                                >
                                    <SpIcon
                                        icon={isNumberVisible ? faEyeSlash : faEye}
                                        color={primary}
                                    />
                                </Pressable>
                            }
                        />
                        :
                        <Box>
                            <Input
                                {...numberField}
                                ref={numberFieldRef}
                                keyboardType={'numberPad'}
                                isInvalid={!!formMethods.errors.number}
                                isDisabled={isLoading}
                                maxLength={19}
                                returnKeyType={'done'}
                                onChangeText={(value) => {

                                    onCardNumberChange(value);
                                }}
                                InputRightElement={
                                    <Pressable
                                        onPress={() => setBooleanState('isNumberVisible', !isNumberVisible)}
                                        px={2}
                                    >
                                        <SpIcon
                                            icon={isNumberVisible ? faEyeSlash : faEye}
                                            color={primary}
                                        />
                                    </Pressable>
                                }
                            />
                            <Pressable
                                position={'absolute'}
                                backgroundColor={field_background}
                                top={1}
                                left={1}
                                right={10}
                                bottom={1}
                                justifyContent={'center'}
                                px={2}
                                onPress={() => {

                                    numberFieldRef.current.focus();
                                    setBooleanState('isNumberFieldFocused', true);
                                }}
                                onBlur={() => setBooleanState('isNumberFieldFocused', false)}
                            >
                                <Caption
                                    fontSize={18}
                                    opacity={.5}
                                >
                                    {'●'.repeat(numberField.value.length)}{isNumberFieldFocused ? '|' : ''}
                                </Caption>
                            </Pressable>
                        </Box>
                    }
                    <FormControl.ErrorMessage
                        leftIcon={<WarningIcon size={3} />}
                    >
                        { formMethods.errors.number?.message }
                    </FormControl.ErrorMessage>
                </FormControl>

                <HStack space={1}>
                    <FormControl flex={1} isInvalid={!!formMethods.errors.cvv}>
                        <FormControl.Label>
                            <Caption text={'credit_card.cvv'} />
                        </FormControl.Label>
                        <Input
                            {...cvvField}
                            keyboardType={'numberPad'}
                            isInvalid={!!formMethods.errors.cvv}
                            isDisabled={isLoading}
                            maxLength={isAmExCard ? 4 : 3}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.cvv?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <FormControl flex={1} isInvalid={!!formMethods.errors.expiry}>
                        <FormControl.Label>
                            <Caption text={'credit_card.expiry'} />
                        </FormControl.Label>
                        <Input
                            {...expiryField}
                            keyboardType={'numberPad'}
                            placeholder={'mm/yy'}
                            isInvalid={!!formMethods.errors.expiry}
                            isDisabled={isLoading}
                            onChangeText={onExpiryChange}
                            maxLength={5}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.expiry?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>

                    <FormControl flex={1} isInvalid={!!formMethods.errors.zip}>
                        <FormControl.Label>
                            <Caption text={'credit_card.zip_code'} />
                        </FormControl.Label>
                        <Input
                            {...zipField}
                            isInvalid={!!formMethods.errors.zip}
                            isDisabled={isLoading}
                            maxLength={7}
                            returnKeyType={'done'}
                        />
                        <FormControl.ErrorMessage
                            leftIcon={<WarningIcon size={3} />}
                        >
                            { formMethods.errors.zip?.message }
                        </FormControl.ErrorMessage>
                    </FormControl>
                </HStack>

                <FormControl.ErrorMessage>
                    <WarningIcon size={3} pr={2} color={'inherit'} />
                    <Caption>
                        { error && error[0] }
                    </Caption>
                </FormControl.ErrorMessage>
                {showSuspendedMethodWarning && 
                    <HStack>
                        <WarningIcon size={3} pr={2} color={'inherit'} />
                        <Caption
                            text={'credit_card.warning'}
                        />
                    </HStack>
                }
                <Caption color='white' textAlign={'center'}>
                    { `Suspended Payment Methods: ${suspendedPaymentMethods.join(', ')}` }
                </Caption>

                <Caption textAlign={'center'}>
                    { summary }
                </Caption>

                <Button
                    onPress={formMethods.handleSubmit(onSubmit)}
                    loading={isLoading}
                    text={submitButtonText || 'general.submit'}
                />

                { showCancel &&
                    <Button
                        variant={'neutral'}
                        onPress={onCancel}
                        loading={isLoading}
                        text={'credit_card.cancel_button'}
                    />
                }
                <Caption
                    textAlign='center'
                    py={2}
                    text={'credit_card.disclaimer'}
                />
            </VStack>
        </FormControl>
    );
};

export default CreditCardForm;
